/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { withMyTheme } from '../theme/theme';
import { mobileCss } from '../theme/isMobile';
import { useState, useEffect } from 'react';
import { MyButton } from '../components/button/MyButton';
import { getImageFromGit } from './getImageFromGit';
import { b64_to_utf8 } from './encodingText';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

export interface OfferImage {
  url: string;
  alt: string;
}

export interface Offer {
  id: number;
  slug: string;
  name: string;
  details_name?: string;
  description: string;
  main_image: OfferImage;
  image_desktop?: OfferImage;
  additional_images?: OfferImage[];
  order: string;
}

interface OffersListProps {
  offers: Offer[];
  onEditOffer: (offer: Offer) => void;
  onAddOffer: (offer: Offer) => void;
  onRemoveOffer: (offer: Offer) => Promise<void>;
  onSignOut: () => void;
}

const TitleStyle = withMyTheme((theme) => css`
  font-size: 2rem;
  font-weight: 600;
  color: ${theme.palette.secondary.contrastText};
  margin-bottom: 2rem;
  text-align: center;
  font-family: ${theme.typography.h1.fontFamily};
`);

const HeaderContainerStyle = withMyTheme((theme) => css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 2rem;
  
  ${mobileCss(`
    flex-direction: column;
    gap: 1rem;
  `)}
`);

const ListContainerStyle = withMyTheme((theme) => css`
  margin: 2rem 0;
  width: 100%;
`);

const OfferItemStyle = withMyTheme((theme) => css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  margin-bottom: 1rem;
  background-color: ${theme.palette.background.paper};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  ${mobileCss(`
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  `)}
`);

const OfferInfoStyle = css`
  display: flex;
  align-items: center;
  flex: 1;
`;

const OfferImageStyle = css`
  width: 60px;
  height: 60px;
  border-radius: 4px;
  object-fit: cover;
  margin-right: 1rem;
`;

const OfferDetailsStyle = withMyTheme((theme) => css`
  flex: 1;
`);

const OfferNameStyle = withMyTheme((theme) => css`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: ${theme.palette.text.primary};
  font-family: ${theme.typography.h1.fontFamily};
`);

const OfferSlugStyle = withMyTheme((theme) => css`
  font-size: 0.9rem;
  color: ${theme.palette.text.secondary};
  margin: 0;
  font-family: ${theme.typography.body1.fontFamily};
`);

const OrderBadgeStyle = withMyTheme((theme) => css`
  background-color: ${theme.palette.primary.main};
  color: ${theme.palette.primary.contrastText};
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  margin-left: 1rem;
`);

const ActionButtonStyle = withMyTheme((theme) => css`
  margin-left: 1rem;
  
  ${mobileCss(`
    align-self: flex-end;
  `)}
`);

const DeleteButtonStyle = withMyTheme((theme) => css`
  margin-left: 1rem;
  background-color: ${theme.palette.error.main};
  color: white;
  padding: 6px 12px;
  min-width: 32px;
  font-size: 0.8rem;
  
  &.MuiButton-contained {
    background-color: ${theme.palette.error.dark};
  }
  &:hover {
    background-color: ${theme.palette.error.dark};
  }
  
  ${mobileCss(`
    align-self: flex-end;
  `)}
`);

const ButtonGroupStyle = css`
  display: flex;
  gap: 8px;
  
  ${mobileCss(`
    width: 100%;
    justify-content: flex-end;
  `)}
`;

const AddButtonStyle = withMyTheme((theme) => css`
  background-color: ${theme.palette.success.main};
  color: white;
  &:hover {
    background-color: ${theme.palette.success.dark};
  }
`);

const SignOutButtonStyle = withMyTheme((theme) => css`
  background-color: ${theme.palette.error.main};
  position: absolute;
  right: 3vw;
  top: 3vw;
  color: white;
  &:hover {
    background-color: ${theme.palette.error.dark};
  }
`);

export const OffersList = ({ offers, onEditOffer, onAddOffer, onRemoveOffer, onSignOut }: OffersListProps) => {
  // Sort offers by order
  const sortedOffers = [...offers].sort((a, b) => parseInt(a.order) - parseInt(b.order))
    .map(offer => ({ ...offer, name: b64_to_utf8(offer.name), description: b64_to_utf8(offer.description) }));

  // State to store image blob URLs
  const [imageUrls, setImageUrls] = useState<Record<number, string>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState<Offer | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Function to create a new empty offer
  const handleAddOffer = () => {
    // Create a new offer with a unique ID
    const maxId = Math.max(...offers.map(offer => offer.id), 0);
    const newId = maxId + 1;
    
    // Create an empty offer template
    const newOffer: Offer = {
      id: newId,
      slug: '',
      name: '',
      description: '',
      main_image: { url: '', alt: '' },
      image_desktop: { url: '', alt: '' },
      order: (offers.length + 1).toString(),
      additional_images: [],
    };
    
    onAddOffer(newOffer);
  };

  // Fetch images for all offers when component mounts or offers change
  useEffect(() => {
    sortedOffers.forEach(offer => {
      if (offer.main_image && offer.main_image?.url) {
        fetchImageWithAuth(offer.main_image.url, offer.id);
      }
    });
  }, [offers]);

  // Function to fetch image with authentication
  const fetchImageWithAuth = async (imagePath: string, offerId: number) => {
    const url = await getImageFromGit(imagePath)
    setImageUrls(prev => ({ ...prev, [offerId]: url }));
  };

  // Function to handle delete button click
  const handleDeleteClick = (offer: Offer) => {
    setOfferToDelete(offer);
    setDeleteDialogOpen(true);
  };

  // Function to handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!offerToDelete) return;

    setIsDeleting(true);
    try {
      await onRemoveOffer(offerToDelete);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting offer:', error);
      alert('Wystąpił błąd podczas usuwania oferty. Spróbuj ponownie.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Function to handle delete cancellation
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setOfferToDelete(null);
  };

  return (
    <div css={ListContainerStyle}>
      <MyButton
            text="Wyloguj się"
            variant="contained"
            additionalCss={SignOutButtonStyle}
            onClick={onSignOut}
          />
      <div css={HeaderContainerStyle}>
        <h2 css={TitleStyle}>Lista ofert</h2>
        <div css={css`display: flex; gap: 12px;`}>
          <MyButton
            text="Dodaj ofertę"
            variant="contained"
            additionalCss={AddButtonStyle}
            onClick={handleAddOffer}
            startIcon={<AddIcon />}
          />
        </div>
      </div>
      {sortedOffers.map((offer) => (
        <div key={offer.id} css={OfferItemStyle}>
          <div css={OfferInfoStyle}>
            {imageUrls[offer.id] && <img
              src={imageUrls[offer.id]}
              alt={offer.main_image?.alt || offer.name}
              css={OfferImageStyle}
            />}
            <div css={OfferDetailsStyle}>
              <h3 css={OfferNameStyle}>
                {offer.name}
                <span css={OrderBadgeStyle}>Kolejność: {offer.order}</span>
              </h3>
              <p css={OfferSlugStyle}>/{offer.slug}</p>
            </div>
          </div>
          <div css={ButtonGroupStyle}>
            <MyButton
              text="Edytuj"
              variant="contained"
              colorVariant="primary"
              additionalCss={ActionButtonStyle}
              onClick={() => onEditOffer(offer)}
              startIcon={<EditIcon />}
            />
            <MyButton
              text="Usuń"
              variant="contained"
              additionalCss={DeleteButtonStyle}
              onClick={() => handleDeleteClick(offer)}
              startIcon={<DeleteIcon fontSize="small" />}
            />
          </div>
        </div>
      ))}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Potwierdź usunięcie
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Czy na pewno chcesz usunąć ofertę "{offerToDelete?.name}"? Ta operacja jest nieodwracalna i usunie również wszystkie powiązane zdjęcia.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary" disabled={isDeleting}>
            Anuluj
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? 'Usuwanie...' : 'Usuń'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
